import { v1 as uuid } from 'uuid';

export const logEvent = (eventName, ...payload) => {
  if (typeof window.champaign === 'undefined') return;

  const opts = {
    page: window.champaign.page.slug,
    plugins: window.champaign.page.plugins,
    layout: window.champaign.page.layout,
    follow_up_layout: window.champaign.page.follow_up_layout,
    new_member: !Object.keys(window.champaign.personalization.member).length,
    ...payload,
  };

  if (window.ga) logToGa(eventName, ...payload);
  if (window.gtag) logToGa4(eventName, ...payload);
};

const getGA4EventData = (eventName, data) => {
  switch (eventName) {
    case 'action:submitted_success':
      return {
        eventName: 'form_submit',
        params: {
          form_id: 'action-form',
          form_name: 'action-form',
          form_destination: data?.action,
          consent: data?.consented || true,
          form_submit_text: data?.submitText,
        },
      };
    case 'fundraiser:donation_completed':
      logGa4Ecommerce(data);
      break;
    case 'social_share':
      return {
        eventName: 'share',
        params: {
          content_type: 'link',
          method: getShareType(data?.share_type),
        },
      };
    case 'form:select_amount':
    case 'change_amount':
      return {
        eventName: 'select_item',
        params: {
          item_list_name: 'donation bands',
          items: [
            {
              item_id: data?.id,
              item_name: data?.text,
              ...(data?.text && {
                price: data.text.match(/\d+/g).map(Number)[0],
              }),
            },
          ],
        },
      };
    case 'two_step:accept':
    case 'two_step:decline':
    case 'donation_button_clicked':
    case 'fundraiser:form:success':
      return {
        eventName: 'button_click',
        params: {
          button_id: data?.id,
          button_text: data?.text,
          action_page_type: getPageType(),
        },
      };
    default:
      return false;
  }
};

const getEventData = (eventName, ...data) => {
  switch (eventName) {
    case 'action:submitted_success':
      return ['action', 'submitted_success'];
    case '@@chmp:consent:change_country':
      return ['gdpr', 'change_country', data[0].countryCode];
    case '@@chmp:consent:change_consent':
      return ['gdpr', 'change_consent', data[0].consented ? 'true' : 'false'];
    case 'form:select_amount':
      return ['fundraising', 'select_amount', data[0].label];
    case 'change_amount':
      return ['fundraising', 'change_amount', data[0].label];
    case 'set_store_in_vault':
      return [
        'fundraising',
        'set_store_in_vault',
        data[0].payload ? 'true' : 'false',
      ];
    case 'set_recurring':
      return [
        'fundraising',
        'set_recurring',
        data[0].payload ? 'true' : 'false',
      ];
    case 'fundraiser:transaction_submitted':
      if (window.ga) logEcommerce(data);
      return [
        'fundraising',
        'transaction_submitted',
        null,
        parseFloat(data[0].value || data[0].amount),
      ];
    case 'fundraiser:one_time_transaction_submitted':
      return ['fundraising', 'one_time_submitted', data[0]];
    case 'fundraiser:monthly_transaction_submitted':
      return ['fundraising', 'monthly_submitted', data[0]];
    case 'fundraiser:weekly_transaction_submitted':
      return ['fundraising', 'weekly_submitted', data[0]];
    case 'fundraiser:one_time_transaction_submitted_forced_layout':
      return [
        'fundraising',
        'one_time_submitted_forced_layout',
        data[0].label,
        parseFloat(data[0].amount),
      ];
    case 'fundraiser:monthly_transaction_submitted_forced_layout':
      return [
        'fundraising',
        'monthly_submitted_forced_layout',
        data[0].label,
        parseFloat(data[0].amount),
      ];
    case 'fundraiser:weekly_transaction_submitted_forced_layout':
      return [
        'fundraising',
        'weekly_submitted_forced_layout',
        data[0].label,
        parseFloat(data[0].amount),
      ];
    case 'fundraiser:set_store_in_vault':
      return ['fundraising', 'set_store_in_vault', data[0]];
    case 'fundraiser:set_one_time':
      return ['fundraising', 'set_one_time', data[0]];
    case 'fundraiser:set_monthly':
      return ['fundraising', 'set_monthly', data[0]];
    case 'fundraiser:set_weekly':
      return ['fundraising', 'set_weekly', data[0]];
    case 'fundraiser:donate_button_clicked_forced_layout':
      return ['fundraising', 'donate_button_clicked_forced_layout', data[0]];
    case 'change_step':
      return [
        'fundraising',
        'donation_form_change_step',
        `step_${data[0].payload + 1}`,
      ];
    case 'change_currency':
      return ['fundraising', 'change_currency', data[0]];
    case 'set_payment_type':
      return ['fundraising', 'set_payment_type', data[0]];
    case 'social_share':
      return ['social_share', 'shared_on_' + data[0].share_type];
    case 'two_step:accept':
      return ['fundraising', 'two_step_accept'];
    case 'two_step:decline':
      return ['fundraising', 'two_step_decline'];
    default:
      return false;
  }
};

const logToGa = (eventName, data) => {
  const eventData = getEventData(eventName, data);

  if (eventData) {
    window.ga('send', 'event', ...eventData);
  }
};

const logToGa4 = (eventName, data) => {
  const eventData = getGA4EventData(eventName, data);
  if (eventData) {
    window.gtag('event', eventData.eventName, eventData.params);
  }
};

const logEcommerce = data => {
  const UUID = uuid();
  window.ga('ecommerce:addTransaction', {
    id: UUID,
    revenue: data[0].value || data[0].amount,
    currency: data[0].currency,
  });

  window.ga('ecommerce:addItem', {
    id: UUID,
    name: data[0].recurring == true ? 'recurring' : 'one-time',
    category: data[0].content_category || 'stored_payment_method',
    price: data[0].value || data[0].amount,
    quantity: 1,
    currency: data[0].currency,
  });

  window.ga('ecommerce:send');
};

const logGa4Ecommerce = data => {
  const UUID = uuid();
  const pageType = getPageType();
  const baseParams = {
    value: data.value || data.amount,
    currency: data.currency,
    items: [
      {
        item_name: data.recurring ? 'recurring' : 'one-time',
        item_category: pageType,
      },
    ],
  };

  const purchase = {
    transaction_id: UUID,
    ...baseParams,
  };

  const paymentInfo = {
    payment_type: data.content_category,
    ...baseParams,
  };

  window.gtag('event', 'purchase', purchase);
  window.gtag('event', 'add_payment_info', paymentInfo);
};

const getShareType = shareType => {
  switch (shareType) {
    case 'f':
      return 'facebook';
    case 't':
      return 'twitter';
    case 'e':
      return 'email';
    case 'w':
      return 'whatsapp';
    default:
      return 'other';
  }
};

const getPageType = () => {
  const petition = 'petition';
  const donation = 'donation';
  const { layout = petition } = champaign?.page || {};

  return layout.toLowerCase().indexOf(petition) > -1 ? petition : donation;
};
